import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
  TooltipProps,
} from 'recharts';
import { cn } from '../../lib/utils';
import { useChartPalette } from './chartColors';

export interface TailwindLineChartSeries {
  dataKey: string;
  label?: string;
  color?: string;
  area?: boolean;
  strokeWidth?: number;
  strokeDasharray?: string;
  showDots?: boolean; // default true
}

export interface TailwindLineChartBrushConfig {
  enabled?: boolean;
  minDataPoints?: number;
  height?: number;
  travellerWidth?: number;
  stroke?: string;
}

export interface TailwindLineChartProps {
  data: any[];
  series: TailwindLineChartSeries[];
  xAxisKey?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  height?: number;
  width?: number | string;
  showGrid?: boolean;
  showLegend?: boolean;
  showTooltip?: boolean;
  className?: string;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  customTooltip?: React.ComponentType<TooltipProps<any, any>>;
  yAxisTickFormatter?: (value: any) => string;
  xAxisTickFormatter?: (value: any) => string;
  xAxisTicks?: (string | number)[];
  xAxisTickFontSize?: number;
  yAxisTickFontSize?: number;
  tooltipValueFormatter?: (value: any, name?: string) => React.ReactNode | [React.ReactNode, React.ReactNode];
  brush?: TailwindLineChartBrushConfig;
}

export default function TailwindLineChart({
  data,
  series,
  xAxisKey = 'name',
  xAxisLabel,
  yAxisLabel,
  height = 400,
  width = '100%',
  showGrid = true,
  showLegend = true,
  showTooltip = true,
  className,
  margin = { top: 5, right: 30, left: 20, bottom: 5 },
  customTooltip,
  yAxisTickFormatter,
  xAxisTickFormatter,
  xAxisTicks,
  xAxisTickFontSize = 12,
  yAxisTickFontSize = 12,
  tooltipValueFormatter,
  brush,
}: TailwindLineChartProps) {
  const palette = useChartPalette();
  const getColor = (index: number) => palette[index % palette.length];
  const showBrush =
    brush?.enabled === true ||
    (brush?.enabled !== false &&
      brush?.minDataPoints != null &&
      data.length > brush.minDataPoints);

  return (
    <div className={cn('w-full', className)}>
      <ResponsiveContainer width={width} height={height}>
        <LineChart data={data} margin={margin}>
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e2e8f0"
              vertical={false}
            />
          )}
          <XAxis
            dataKey={xAxisKey}
            label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -5 } : undefined}
            tick={{ fill: '#64748b', fontSize: xAxisTickFontSize }}
            stroke="#cbd5e1"
            ticks={xAxisTicks}
            tickFormatter={xAxisTickFormatter}
          />
          <YAxis
            label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft' } : undefined}
            tick={{ fill: '#64748b', fontSize: yAxisTickFontSize }}
            stroke="#cbd5e1"
            tickFormatter={yAxisTickFormatter}
          />
          {showTooltip && (
            customTooltip ? (
              <Tooltip content={customTooltip} />
            ) : (
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '0.5rem',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                }}
                labelStyle={{ color: '#1e293b', fontWeight: 600 }}
                formatter={tooltipValueFormatter as any}
              />
            )
          )}
          {showLegend && (
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="line"
            />
          )}
          {series.map((s, index) => (
            <Line
              key={s.dataKey}
              type="monotone"
              dataKey={s.dataKey}
              name={s.label || s.dataKey}
              stroke={s.color || getColor(index)}
              strokeWidth={s.strokeWidth || 2}
              strokeDasharray={s.strokeDasharray}
              dot={s.showDots === false ? false : { r: 3 }}
              activeDot={s.showDots === false ? false : { r: 5 }}
            />
          ))}
          {showBrush && (
            <Brush
              dataKey={xAxisKey}
              height={brush?.height ?? 24}
              travellerWidth={brush?.travellerWidth ?? 8}
              stroke={brush?.stroke ?? (series[0]?.color || getColor(0))}
              tickFormatter={xAxisTickFormatter}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
